import React, { useEffect, useState } from 'react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  LineChart, Line, XAxis, YAxis, CartesianGrid, ReferenceLine,
  ResponsiveContainer, Tooltip, Legend,
} from 'recharts';
import { api } from '../../lib/api';
import { SectionHeader } from '../../components/UI';
import { DataSourceBadge } from '../../components/sales/DataSourceBadge';
import { ClockLabel } from '../../components/sales/ClockLabel';
import { TimeSeriesTile } from '../../components/sales/TimeSeriesTile';
import { RefreshCw, Radio, Sparkles, AlertTriangle, LineChart as LineChartIcon } from 'lucide-react';
import { toast } from 'sonner';

// M3 · Sales Console — "What's my funnel, lead quality, and call quality right now?"
// Ingests Slack Tier 1 channels: celebrations-calls, celebrations-sales,
// sales-no-shows, call-intelligence. Any channel not yet wired renders a
// "waiting for bot invite" chip rather than fabricating values.

const CHART_TOOLTIP = { background: '#FFFFFF', border: '1px solid #141B4D', color: '#141B4D', borderRadius: 0, fontSize: 12 };
const CHART_GRID = { strokeDasharray: '2 4', stroke: '#141B4D', strokeOpacity: 0.1 };
const CHART_AXIS = { stroke: '#141B4D', strokeOpacity: 0.3 };

function ChannelStatusRow({ ch, k }) {
  const online = ch.id && (ch.count ?? 0) > 0;
  return (
    <li className="flex items-center justify-between py-2 border-t border-indie-ink/10 first:border-t-0" data-testid={`console-channel-${k}`}>
      <div className="flex items-center gap-2">
        <span className={`inline-block w-2 h-2 rounded-full ${online ? 'bg-emerald-600' : 'bg-amber-500'}`} />
        <span className="text-sm text-indie-ink font-mono">{ch.name}</span>
        {!ch.id && (
          <span className="text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 bg-amber-500 text-indie-ink font-semibold">
            pending invite
          </span>
        )}
      </div>
      <div className="text-xs text-indie-ink/70">
        {ch.id
          ? `${ch.parsed ?? 0} / ${ch.count ?? 0} parsed`
          : ch.note}
      </div>
    </li>
  );
}

// BantHeatmapCell + old radar constants (RADAR_DIMS, RADAR_COLORS, teamRadar,
// radarData, teamN, anyScores) were removed alongside the Slack-derived
// tiles when the Scorecard sheet became the authoritative call-scoring source.

export default function SalesConsole() {
  const [d, setD] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [mtd, setMtd] = useState(null);
  const [ts, setTs] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [scorecard, setScorecard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = () => Promise.all([
    api.get('/sales/console/snapshot'),
    api.get('/sales/console/monthly').catch(() => ({ data: null })),
    api.get('/sales/console/mtd').catch(() => ({ data: null })),
    api.get('/sales/timeseries').catch(() => ({ data: null })),
    api.get('/sales/dashboard').catch(() => ({ data: null })),
    api.get('/sales/scorecard/summary').catch(() => ({ data: null })),
  ]).then(([s, m, mp, t, dash, sc]) => {
    setD(s.data); setMonthly(m.data); setMtd(mp.data); setTs(t.data); setDashboard(dash.data); setScorecard(sc.data);
  }).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const runSync = async () => {
    setSyncing(true);
    try {
      const r = await api.post('/sales/console/sync');
      if (r.data.ok) {
        toast.success('Console synced.');
        load();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed.');
    } finally {
      setSyncing(false);
    }
  };

  if (loading) return <div className="label-editorial" data-testid="console-loading">Loading Sales Console…</div>;
  if (!d || !d.aggregate) return <div className="label-editorial">No console data yet.</div>;

  const agg = d.aggregate || {};
  const closers = agg.per_closer || [];
  const funnel = agg.funnel || {};
  const bySource = agg.by_source || {};
  const perChannel = d.per_channel || {};
  const lastSync = d.last_synced ? new Date(d.last_synced).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <div className="space-y-6" data-testid="sales-console-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <SectionHeader
          eyebrow="Sales · Console"
          title="What's my funnel and call quality right now?"
          description="Live MTD funnel from Slack Tier 1, month-over-month history from BHAG, and authoritative call scoring / BANT / viability from the #call-intelligence Scorecard sheet."
        />
        <div className="flex items-center gap-2">
          <ClockLabel type="cohort" />
          <DataSourceBadge
            source="Slack Tier 1"
            asOf={lastSync}
            confidence={d.parse_failure_count > 30 ? 'amber' : 'green'}
            testId="console-source-badge"
          />
          <button
            data-testid="console-sync-btn"
            onClick={runSync}
            disabled={syncing}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.14em] font-semibold bg-indie-navy text-indie-cream px-3 py-2 disabled:opacity-50"
          >
            <RefreshCw size={12} className={syncing ? 'animate-spin' : ''} />
            {syncing ? 'Syncing…' : 'Sync'}
          </button>
        </div>
      </div>

      {/* MTD hero — Sales Console owns "current month" per the Feb-2026
          temporal-role rewrite. Cumulative revenue day-by-day comes from
          #dailyhighlight (parsed to `daily_pulse`), plotted against
          baseline/stretch targets and last month's cumulative curve as a
          faint reference. Cash clock. Never blends with cohort. */}
      {mtd && (
        <div className="border border-indie-ink/15 bg-white p-5" data-testid="console-mtd-hero">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
                MTD pace · {mtd.month_label}
              </div>
              <div className="mt-1 text-3xl font-semibold text-indie-ink num-tabular" data-testid="mtd-revenue">
                {mtd.latest?.mtd_revenue != null ? `$${Math.round(mtd.latest.mtd_revenue).toLocaleString()}` : '—'}
              </div>
              <div className="text-[11px] text-indie-ink/60">
                {mtd.baseline_target ? `Baseline $${Math.round(mtd.baseline_target).toLocaleString()}` : ''}
                {mtd.stretch_target ? ` · Stretch $${Math.round(mtd.stretch_target).toLocaleString()}` : ''}
                {mtd.prior_month_final ? ` · Prior mo. final $${Math.round(mtd.prior_month_final).toLocaleString()}` : ''}
              </div>
            </div>
            <div className="text-right">
              {mtd.latest?.workdays_left != null && (
                <div className="text-xs text-indie-ink/70">
                  {mtd.latest.workdays_left} workday{mtd.latest.workdays_left === 1 ? '' : 's'} left
                </div>
              )}
              {mtd.latest?.required_daily_stretch != null && (
                <div className="text-xs text-indie-ink/70 num-tabular">
                  Stretch pace: ${Math.round(mtd.latest.required_daily_stretch).toLocaleString()}/day
                </div>
              )}
              {mtd.latest?.required_daily_baseline != null && (
                <div className="text-xs text-indie-ink/70 num-tabular">
                  Baseline pace: ${Math.round(mtd.latest.required_daily_baseline).toLocaleString()}/day
                </div>
              )}
            </div>
          </div>
          <div className="mt-4 h-56" data-testid="console-mtd-chart">
            <ResponsiveContainer>
              <LineChart data={mtd.series || []} margin={{ top: 6, right: 8, bottom: 4, left: 4 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#141B4D" strokeOpacity={0.1} />
                <XAxis dataKey="day" tick={{ fontSize: 10, fill: '#141B4D' }} stroke="#141B4D" strokeOpacity={0.3} />
                <YAxis tick={{ fontSize: 10, fill: '#141B4D' }} stroke="#141B4D" strokeOpacity={0.3} width={54}
                       tickFormatter={(v) => `$${Math.round(v / 1000)}k`} />
                <Tooltip contentStyle={CHART_TOOLTIP}
                         formatter={(v, name) => [v == null ? '—' : `$${Math.round(v).toLocaleString()}`, name]}
                         labelFormatter={(l) => `Day ${l}`} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {mtd.baseline_target != null && (
                  <ReferenceLine y={mtd.baseline_target} stroke="#8A8A8A" strokeDasharray="4 4"
                    label={{ value: 'Baseline', position: 'insideTopRight', fontSize: 9, fill: '#8A8A8A' }} />
                )}
                {mtd.stretch_target != null && (
                  <ReferenceLine y={mtd.stretch_target} stroke="#1D57A5" strokeDasharray="4 4"
                    label={{ value: 'Stretch', position: 'insideTopRight', fontSize: 9, fill: '#1D57A5' }} />
                )}
                <Line type="monotone" dataKey="prior" name={mtd.prior_month_label || 'Prior month'}
                      stroke="#141B4D" strokeOpacity={0.35} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls />
                <Line type="monotone" dataKey="mtd_revenue" name="This month cumulative"
                      stroke="#141B4D" strokeWidth={2.5} dot={{ r: 2 }} isAnimationActive={false} connectNulls />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="console-mtd-funnel">
            {[
              { key: 'booked', label: 'Booked' },
              { key: 'held', label: 'Held' },
              { key: 'no_shows', label: 'No-shows' },
              { key: 'no_show_rate', label: 'No-show rate', fmt: (v) => v == null ? '—' : `${(v * 100).toFixed(1)}%` },
            ].map((tile) => (
              <div key={tile.key} className="p-3 bg-indie-cream border border-indie-ink/10"
                   data-testid={`mtd-funnel-${tile.key}`}>
                <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">{tile.label}</div>
                <div className="mt-1 text-xl font-semibold text-indie-ink num-tabular">
                  {tile.fmt ? tile.fmt(mtd.funnel_mtd?.[tile.key]) : (mtd.funnel_mtd?.[tile.key] ?? '—')}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-3 text-[10px] text-indie-ink/50 pt-1 border-t border-indie-ink/10">
            Source: {mtd.source} · Cash clock
          </div>
        </div>
      )}

      {/* Alert strip — what changed since yesterday. Moved here from
          Dashboard per Feb-2026 vision — alerts are a "right now" signal. */}
      {dashboard?.alerts?.length > 0 && (
        <div className="border-l-4 border-rose-500 bg-white p-4" data-testid="console-alerts">
          <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold mb-2">
            Did anything break yesterday?
          </div>
          <ul className="space-y-1 text-sm text-indie-ink">
            {dashboard.alerts.map((a, i) => (
              <li key={i} data-testid={`console-alert-${i}`}>· {a}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Google Ads MTD strip — moved from Dashboard */}
      {dashboard?.google_ads && (
        <div className="border border-indie-ink/15 bg-white p-4" data-testid="console-google-ads-mtd">
          <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold mb-2">
            Google Ads · MTD
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div><div className="text-[10px] text-indie-ink/50">Spend</div>
                 <div className="text-lg font-semibold text-indie-ink num-tabular">
                   {dashboard.google_ads.spend_mtd != null ? `$${Math.round(dashboard.google_ads.spend_mtd).toLocaleString()}` : '—'}
                 </div></div>
            <div><div className="text-[10px] text-indie-ink/50">Revenue</div>
                 <div className="text-lg font-semibold text-indie-ink num-tabular">
                   {dashboard.google_ads.revenue_mtd != null ? `$${Math.round(dashboard.google_ads.revenue_mtd).toLocaleString()}` : '—'}
                 </div></div>
            <div><div className="text-[10px] text-indie-ink/50">ROAS</div>
                 <div className="text-lg font-semibold text-indie-ink num-tabular">
                   {dashboard.google_ads.roas_mtd != null ? `${dashboard.google_ads.roas_mtd.toFixed(2)}x` : '—'}
                 </div></div>
            <div><div className="text-[10px] text-indie-ink/50">Breakeven</div>
                 <div className="text-lg font-semibold text-indie-ink num-tabular">1.18x</div></div>
          </div>
        </div>
      )}

      {/* Month-over-month trends — closed months from BHAG time-series.
          Answers "how does this month compare to prior months?" without
          waiting on Console-Slack snapshots to accumulate. Sourced from
          the shared /sales/timeseries payload (BHAG-authoritative for
          Revenue + Sales + Calls Booked + Calls Held; derived for rates). */}
      {ts?.metrics && (
        <div className="border border-indie-ink/15 bg-white p-5" data-testid="console-mom-trends">
          <div className="flex items-center gap-2 flex-wrap">
            <LineChartIcon size={14} className="text-indie-ink/70" strokeWidth={1.5} />
            <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
              Month-over-month · closed months
            </div>
            <ClockLabel type="cash" />
            <span className="text-[10px] text-indie-ink/50">
              through {ts.last_closed_month || 'last complete month'} · MoM delta above every chart
            </span>
          </div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="console-mom-grid">
            {['revenue', 'sales_count', 'calls_booked', 'calls_held', 'qualification_rate', 'close_rate', 'no_show_rate', 'avg_deal_price']
              .map((k) => (ts.metrics || []).find((m) => m.key === k))
              .filter(Boolean)
              .map((m) => (
                <TimeSeriesTile
                  key={m.key}
                  metric={m}
                  methodologyBreaks={ts.methodology_breaks}
                  color="#141B4D"
                  goalColor="#1D57A5"
                  compact
                  testId={`console-mom-${m.key}`}
                />
              ))}
          </div>
          <div className="mt-3 text-[10px] text-indie-ink/50 pt-2 border-t border-indie-ink/10">
            Source: BHAG monthly (authoritative for Revenue/Sales/Calls). Rate charts pinned to 0–100%. Dashed = plan (original for revenue) · solid = actual.
          </div>
        </div>
      )}

      {/* ------------------------------------------------------------------
          Scorecard from #call-intelligence sheet (Feb 2026 M8).
          Authoritative source for call-scoring, BANT and viability — the
          sheet has full history back to March 2026 (vs Slack's 90-day
          window), no parse failures, and HubSpot deep-links attached.
          Slack `#call-intelligence` parsing still runs silently for
          reconciliation; mismatches surface on Integrity.
          ------------------------------------------------------------------ */}
      {scorecard?.meta && (
        <div className="border-2 border-indie-navy bg-white p-5 space-y-5" data-testid="console-scorecard">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
                Scorecard · #call-intelligence sheet · authoritative
              </div>
              <div className="mt-1 text-lg font-semibold text-indie-ink leading-snug">
                Every consult scored, indexed, and deep-linked to HubSpot
              </div>
              <div className="mt-1 text-[11px] text-indie-ink/60">
                {scorecard.meta.row_count_ingested?.toLocaleString()} rows since March 2026 · zero parse failures · synced {scorecard.meta.last_synced ? new Date(scorecard.meta.last_synced).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—'}
              </div>
            </div>
            <DataSourceBadge
              source="Google Sheet · Scorecard tab"
              asOf={scorecard.meta.last_synced ? new Date(scorecard.meta.last_synced).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
              confidence="green"
              testId="scorecard-source"
            />
          </div>

          {/* Monthly avg-score trend · per closer + team */}
          <div className="border border-indie-ink/10 bg-white p-4" data-testid="scorecard-monthly">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
                Monthly average score · per closer · scale 1-5
              </div>
              <span className="text-[10px] text-indie-ink/50">min 3 scored calls to show an average</span>
            </div>
            <div className="mt-3 h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={(scorecard.monthly_avg_score.months || []).map((ym, i) => {
                    const row = { month: ym };
                    for (const rep of ['Chi', 'Paul', 'Garret']) {
                      const p = scorecard.monthly_avg_score.per_closer[rep]?.[i];
                      row[rep] = (p && p.n >= 3) ? p.avg : null;
                    }
                    const tp = scorecard.monthly_avg_score.team[i];
                    row['Team'] = (tp && tp.n >= 3) ? tp.avg : null;
                    return row;
                  })}
                  margin={{ left: 20, right: 20, top: 20, bottom: 10 }}
                >
                  <CartesianGrid {...CHART_GRID} />
                  <XAxis dataKey="month" {...CHART_AXIS} tick={{ fontSize: 11 }} />
                  <YAxis {...CHART_AXIS} tick={{ fontSize: 11 }} domain={[1, 5]} />
                  <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => v == null ? '—' : v.toFixed(2)} />
                  <Legend iconType="rect" wrapperStyle={{ fontSize: 11 }} />
                  <Line type="monotone" dataKey="Team"   stroke="#141B4D" strokeWidth={3} strokeDasharray="4 4" dot={{ r: 3 }} connectNulls />
                  <Line type="monotone" dataKey="Chi"    stroke="#1D57A5" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  <Line type="monotone" dataKey="Paul"   stroke="#0F766E" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                  <Line type="monotone" dataKey="Garret" stroke="#B4530C" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-2 text-[10px] text-indie-ink/50">
              N per closer per month (scored calls): {(scorecard.monthly_avg_score.months || []).map((ym, i) => {
                const parts = ['Chi', 'Paul', 'Garret'].map((r) => {
                  const p = scorecard.monthly_avg_score.per_closer[r]?.[i];
                  return `${r} ${p?.n || 0}`;
                });
                return `${ym.slice(-2)}: ${parts.join(' / ')}`;
              }).join(' · ')}
            </div>
          </div>

          {/* Skills breakdown radar — trailing 30d, per closer */}
          <div className="border border-indie-ink/10 bg-white p-4" data-testid="scorecard-skills">
            <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
              Skills breakdown · trailing {scorecard.skills.window_days} days · scale 1-5
            </div>
            <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
              {['Chi', 'Paul', 'Garret'].map((rep) => {
                const recent = scorecard.skills.per_closer[rep]?.recent || {};
                const allTime = scorecard.skills.per_closer[rep]?.all_time || {};
                const dims = [
                  ['Discovery', 'discovery'],
                  ['Rapport', 'rapport'],
                  ['Value Framing', 'value_framing'],
                  ['Objection Handling', 'objection_handling'],
                  ['Close', 'close'],
                ];
                const data = dims.map(([label, key]) => ({
                  dim: label,
                  Recent: recent[key]?.avg ?? null,
                  AllTime: allTime[key]?.avg ?? null,
                }));
                const n = recent.discovery?.n || 0;
                return (
                  <div key={rep} className="border border-indie-ink/10 p-3" data-testid={`scorecard-skills-${rep.toLowerCase()}`}>
                    <div className="flex items-baseline justify-between">
                      <div className="text-sm font-semibold text-indie-ink">{rep}</div>
                      <div className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/50">
                        n={n} calls · 30d
                      </div>
                    </div>
                    {n === 0 ? (
                      <div className="mt-3 h-40 flex items-center justify-center text-xs text-indie-ink/50 italic">
                        No scored calls in trailing 30 days
                      </div>
                    ) : (
                      <div className="mt-2 h-40">
                        <ResponsiveContainer width="100%" height="100%">
                          <RadarChart data={data}>
                            <PolarGrid stroke="#DDD" />
                            <PolarAngleAxis dataKey="dim" tick={{ fontSize: 9 }} />
                            <PolarRadiusAxis domain={[0, 5]} tick={{ fontSize: 8 }} />
                            <Radar name="Trailing 30d" dataKey="Recent"  stroke="#1D57A5" fill="#1D57A5" fillOpacity={0.28} />
                            <Radar name="All-time"     dataKey="AllTime" stroke="#141B4D" fill="none" strokeDasharray="4 4" />
                            <Tooltip contentStyle={CHART_TOOLTIP} formatter={(v) => v == null ? '—' : Number(v).toFixed(2)} />
                          </RadarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="mt-2 text-[10px] text-indie-ink/50">
              Recent = trailing 30 days · All-time dashed overlay for drift detection. Skills are the 5 columns scored in the #call-intelligence sheet.
            </div>
          </div>

          {/* BANT pass-rate bars · per closer · per element */}
          <div className="border border-indie-ink/10 bg-white p-4" data-testid="scorecard-bant">
            <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
              BANT pass-rate · per element · flags started 2026-07-28
            </div>
            <div className="mt-3 space-y-3">
              {['Chi', 'Paul', 'Garret', '__team__'].map((key) => {
                const src = key === '__team__' ? scorecard.bant.team : scorecard.bant.per_closer[key];
                const label = key === '__team__' ? 'Team' : key;
                const anyPop = Object.values(src || {}).some((v) => v.populated > 0);
                if (!anyPop) {
                  // Keep the closer's row visible with a muted placeholder so
                  // the 4-closer layout stays consistent as BANT flags ramp
                  // up (started 2026-07-28 for Chi/Paul first, Garret
                  // still ramping).
                  return (
                    <div key={key} data-testid={`scorecard-bant-${key.toLowerCase()}`}>
                      <div className="flex items-baseline justify-between mb-1">
                        <div className="text-xs font-medium text-indie-ink/60">{label}</div>
                        <div className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/40">no BANT-populated calls yet</div>
                      </div>
                      <div className="grid grid-cols-4 gap-2 opacity-40">
                        {['need', 'financial', 'authority', 'timing'].map((el) => (
                          <div key={el} className="text-[10px]">
                            <div className="flex justify-between items-baseline">
                              <span className="text-indie-ink/50 capitalize">{el}</span>
                              <span className="text-indie-ink/40 num-tabular">—</span>
                            </div>
                            <div className="mt-0.5 h-2 bg-indie-cream border border-indie-ink/10" />
                            <div className="text-[9px] text-indie-ink/30 num-tabular">0/0</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                return (
                  <div key={key} data-testid={`scorecard-bant-${key.toLowerCase()}`}>
                    <div className="flex items-baseline justify-between mb-1">
                      <div className={`text-xs ${key === '__team__' ? 'font-semibold text-indie-ink' : 'font-medium text-indie-ink/80'}`}>{label}</div>
                      <div className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/50">
                        n={Object.values(src || {})[0]?.populated || 0} SQL/MQL rows
                      </div>
                    </div>
                    <div className="grid grid-cols-4 gap-2">
                      {['need', 'financial', 'authority', 'timing'].map((el) => {
                        const v = src[el];
                        const rate = v?.rate;
                        const barPct = rate == null ? 0 : rate * 100;
                        return (
                          <div key={el} className="text-[10px]">
                            <div className="flex justify-between items-baseline">
                              <span className="text-indie-ink/70 capitalize">{el}</span>
                              <span className="font-semibold num-tabular text-indie-ink">
                                {rate == null ? '—' : `${Math.round(rate * 100)}%`}
                              </span>
                            </div>
                            <div className="mt-0.5 h-2 bg-indie-cream border border-indie-ink/10">
                              <div className={`h-full ${rate >= 0.9 ? 'bg-emerald-600' : rate >= 0.75 ? 'bg-indie-cobalt' : 'bg-rose-500'}`} style={{ width: `${barPct}%` }} />
                            </div>
                            <div className="text-[9px] text-indie-ink/40 num-tabular">{v?.pass}/{v?.populated}</div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Viability funnel · lifetime totals per closer */}
          <div className="border border-indie-ink/10 bg-white p-4" data-testid="scorecard-viability">
            <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
              Viability mix · lifetime totals from Lead Quality column
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/50 font-semibold border-b border-indie-ink/10">
                    <th className="text-left py-2 pr-3">Closer</th>
                    {['SQL', 'MQL', 'Warm', 'Pre-qualified', 'Cold/Curious', 'Non-viable', 'Unclassified'].map((b) => (
                      <th key={b} className="text-right py-2 px-2">{b}</th>
                    ))}
                    <th className="text-right py-2 pl-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {['Chi', 'Paul', 'Garret'].map((rep) => {
                    const t = scorecard.viability.per_closer[rep]?.totals || {};
                    const total = Object.values(t).reduce((s, v) => s + v, 0);
                    return (
                      <tr key={rep} className="border-b border-indie-ink/5" data-testid={`scorecard-viability-${rep.toLowerCase()}`}>
                        <td className="py-2 pr-3 text-indie-ink font-medium">{rep}</td>
                        {['SQL', 'MQL', 'Warm', 'Pre-qualified', 'Cold/Curious', 'Non-viable', 'Unclassified'].map((b) => (
                          <td key={b} className="py-2 px-2 text-right num-tabular">{t[b] || 0}</td>
                        ))}
                        <td className="py-2 pl-2 text-right num-tabular font-semibold">{total}</td>
                      </tr>
                    );
                  })}
                  <tr className="bg-indie-cream/60">
                    <td className="py-2 pr-3 text-[10px] uppercase tracking-[0.12em] text-indie-ink/60 font-semibold">Team</td>
                    {['SQL', 'MQL', 'Warm', 'Pre-qualified', 'Cold/Curious', 'Non-viable', 'Unclassified'].map((b) => (
                      <td key={b} className="py-2 px-2 text-right num-tabular text-indie-ink/80">{scorecard.viability.team.totals[b] || 0}</td>
                    ))}
                    <td className="py-2 pl-2 text-right num-tabular font-semibold">
                      {Object.values(scorecard.viability.team.totals || {}).reduce((s, v) => s + v, 0)}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div className="mt-2 text-[10px] text-indie-ink/50">
              Unclassified is expected to be large for Mar–early-Jul rows where Lead Quality wasn&apos;t tagged yet — the sheet started using the standard SQL/MQL/etc taxonomy from mid-July 2026 onward.
            </div>
          </div>

          {/* Recent scored calls feed with HubSpot deep-links */}
          <div className="border border-indie-ink/10 bg-white p-4" data-testid="scorecard-recent">
            <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">
              Recent scored calls · last {scorecard.recent_calls.length}
            </div>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-[10px] uppercase tracking-[0.12em] text-indie-ink/50 font-semibold border-b border-indie-ink/10">
                    <th className="text-left py-2 pr-3">Date</th>
                    <th className="text-left py-2 px-2">Closer</th>
                    <th className="text-left py-2 px-2">Lead</th>
                    <th className="text-right py-2 px-2">Min</th>
                    <th className="text-left py-2 px-2">Quality</th>
                    <th className="text-right py-2 px-2">Avg</th>
                    <th className="text-left py-2 pl-2">HubSpot</th>
                  </tr>
                </thead>
                <tbody>
                  {scorecard.recent_calls.map((c) => (
                    <tr key={c.row_hash} className="border-b border-indie-ink/5" data-testid={`scorecard-recent-${c.row_hash.slice(0, 8)}`}>
                      <td className="py-1.5 pr-3 num-tabular text-indie-ink/70 whitespace-nowrap">{c.date?.slice(0, 10)}</td>
                      <td className="py-1.5 px-2 text-indie-ink font-medium">{c.rep || '—'}</td>
                      <td className="py-1.5 px-2 text-indie-ink/80 truncate max-w-[240px]" title={c.lead_name}>{c.lead_name || '—'}</td>
                      <td className="py-1.5 px-2 text-right num-tabular text-indie-ink/70">{c.duration_min ?? '—'}</td>
                      <td className="py-1.5 px-2 text-indie-ink/70">
                        {c.lead_quality && (
                          <span className={`text-[10px] uppercase tracking-[0.1em] px-1.5 py-0.5 border ${
                            c.lead_quality === 'SQL' ? 'border-emerald-600 text-emerald-700 bg-emerald-50' :
                            c.lead_quality === 'MQL' ? 'border-indie-cobalt text-indie-cobalt bg-indie-cobalt/10' :
                            c.lead_quality === 'Non-viable' ? 'border-rose-500 text-rose-700 bg-rose-50' :
                            'border-indie-ink/20 text-indie-ink/70 bg-indie-cream/60'
                          }`}>{c.lead_quality}</span>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-right num-tabular font-semibold text-indie-ink">
                        {c.avg_score != null ? c.avg_score.toFixed(1) : '—'}
                      </td>
                      <td className="py-1.5 pl-2">
                        {c.hubspot_url ? (
                          <a href={c.hubspot_url} target="_blank" rel="noopener noreferrer"
                             className="text-[10px] uppercase tracking-[0.12em] text-indie-cobalt hover:underline">
                            open ↗
                          </a>
                        ) : (
                          <span className="text-[10px] text-indie-ink/30">no link</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="text-[10px] text-indie-ink/50 pt-1 border-t border-indie-ink/10">
            Source: <a className="underline hover:text-indie-cobalt" href={scorecard.meta.source_url} target="_blank" rel="noopener noreferrer">Google Sheet · Scorecard tab</a> ({scorecard.meta.row_count_ingested?.toLocaleString()} rows). Auto-populated after every #call-intelligence Slack post. Rep name &quot;Garrett Orr&quot; normalised to &quot;Garret&quot; per firm convention.
          </div>
        </div>
      )}




      {/* Accumulation notice — Console history begins at first sync date.
          Persisted monthly via /api/sales/console/monthly on every sync;
          trend charts light up when ≥ 2 months exist. Rule 8 hidden panels
          are surfaced here so gaps are visible. */}
      <div className="border border-indie-ink/25 bg-indie-cream/60 p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2" data-testid="console-history-notice">
        <div className="text-xs text-indie-ink/80">
          <span className="font-semibold text-indie-ink">History accumulating</span>{' '}
          {monthly?.history_starts
            ? <>since <span className="font-mono">{monthly.history_starts}</span> · <span className="font-mono">{monthly.count}</span> monthly snapshot{monthly.count === 1 ? '' : 's'} captured</>
            : 'from this sync onward'}
          . Slack Tier 1 + MQL/SQL rubric began late July 2026 — pre-July history cannot be backfilled and is not simulated.
        </div>
        {monthly?.count < 2 && (
          <span className="text-[10px] uppercase tracking-[0.14em] px-1.5 py-0.5 bg-amber-500 text-indie-ink font-semibold self-start md:self-auto">
            trend charts unlock at 2 months
          </span>
        )}
      </div>

      {/* Channel health */}
      <div className="border border-indie-ink/15 bg-white p-5" data-testid="console-channels">
        <div className="flex items-center gap-2">
          <Radio size={14} className="text-indie-ink/70" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">Tier 1 channel health</div>
        </div>
        <ul className="mt-3">
          {Object.entries(perChannel).map(([k, ch]) => <ChannelStatusRow key={k} k={k} ch={ch} />)}
        </ul>
        {d.parse_failure_count > 0 && (
          <div className="mt-3 text-[11px] text-indie-ink/60 flex items-center gap-1">
            <AlertTriangle size={12} className="text-amber-500" />
            {d.parse_failure_count} unparseable messages routed to Integrity (parse-failure log).
          </div>
        )}
      </div>

      {/* Trailing 14-day funnel — kept as secondary context under MTD */}
      <div data-testid="console-trailing-14">
        <div className="mb-2 text-[10px] uppercase tracking-[0.14em] text-indie-ink/50 font-semibold">
          Trailing 14-day context
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="console-funnel">
          {[
            { key: 'booked',     label: 'Booked calls (14d)', src: '#celebrations-calls' },
            { key: 'no_shows',   label: 'No-shows (14d)',      src: '#sales-no-shows' },
            { key: 'sales',      label: 'Sales (14d)',         src: '#celebrations-sales' },
          ].map((tile) => (
            <div key={tile.key} className="border border-indie-ink/15 bg-white p-4">
              <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold">{tile.label}</div>
              <div className="mt-1 text-3xl font-semibold text-indie-ink">{funnel[tile.key] ?? '—'}</div>
              <div className="mt-1 text-[10px] text-indie-ink/50 font-mono">{tile.src}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Source × outcome — from #celebrations-calls */}
      <div className="border border-indie-ink/15 bg-white p-5" data-testid="console-by-source">
        <div className="flex items-center gap-2">
          <Sparkles size={14} className="text-indie-ink/70" strokeWidth={1.5} />
          <div className="text-[10px] uppercase tracking-[0.16em] text-indie-ink/60 font-semibold">
            Booked calls by source · last 14 days
          </div>
        </div>
        <div className="mt-3 space-y-1">
          {Object.entries(bySource).length === 0 && (
            <div className="text-sm text-indie-ink/60 italic">No source data — waiting for #celebrations-calls to accumulate.</div>
          )}
          {Object.entries(bySource)
            .sort(([, a], [, b]) => b - a)
            .map(([source, count]) => {
              const total = Object.values(bySource).reduce((s, v) => s + v, 0);
              const pct = ((count / total) * 100).toFixed(0);
              return (
                <div key={source} className="flex items-center gap-3 text-sm" data-testid={`console-source-${source.toLowerCase().replace(/\s+/g,'-')}`}>
                  <div className="w-40 text-indie-ink/80">{source}</div>
                  <div className="flex-1 bg-indie-cream h-6 relative">
                    <div className="bg-indie-cobalt h-full" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-20 text-right font-mono text-xs">{count} · {pct}%</div>
                </div>
              );
            })}
        </div>
      </div>


      {/* Hidden-panels notice (rule 8): tracks Console-related surfaces
          that intentionally aren't rendered until their source is fixed. */}
      {ts?.hidden_panels?.length > 0 && (
        <div className="border border-dashed border-indie-ink/25 bg-indie-cream/50 p-4" data-testid="console-hidden-panels">
          <div className="text-[10px] uppercase tracking-[0.14em] text-indie-ink/60 font-semibold mb-2">
            Panels intentionally hidden ({ts.hidden_panels.length})
          </div>
          <ul className="text-xs text-indie-ink/70 space-y-1">
            {ts.hidden_panels.map((p) => (
              <li key={p.panel} data-testid={`console-hidden-panel-${p.panel}`}>
                <span className="font-mono text-indie-ink/50">{p.panel}</span> · {p.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="text-[11px] text-indie-ink/50 pt-2 border-t border-indie-ink/10">
        Live from Slack Tier 1 · reconciled against HubSpot contacts (0.75 confidence threshold on name+email joins). Any unresolved event lands on Integrity.
      </div>
    </div>
  );
}
